'use client'

import { signIn } from '@/shared/ui/providers/AuthSessionProvider'
import { useTranslations } from '@/lib/next-shim'
import { getEnv } from '@/lib/env'

interface SocialProvider {
  alias: string
  name: string
  icon: React.ReactNode
}

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

const providerIcons: Record<string, React.ReactNode> = {
  google: <GoogleIcon />,
}

const providerNames: Record<string, string> = {
  google: 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  microsoft: 'Microsoft',
}

function getConfiguredProviders(): SocialProvider[] {
  const envValue = getEnv('NEXT_PUBLIC_SOCIAL_PROVIDERS', '')
  if (!envValue) return []

  return envValue
    .split(',')
    .map((alias) => alias.trim())
    .filter(Boolean)
    .map((alias) => ({
      alias,
      name: providerNames[alias] || alias,
      icon: providerIcons[alias] || null,
    }))
}

interface SocialLoginButtonsProps {
  callbackUrl?: string
}

export function SocialLoginButtons({ callbackUrl }: SocialLoginButtonsProps) {
  const t = useTranslations()
  const providers = getConfiguredProviders()

  if (providers.length === 0) return null

  const handleSocialLogin = (alias: string) => {
    signIn('keycloak', {
      idpHint: alias,
      callbackUrl: callbackUrl || '/account/dashboard',
    })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {providers.map((provider) => (
          <button
            key={provider.alias}
            type="button"
            onClick={() => handleSocialLogin(provider.alias)}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
          >
            {provider.icon}
            {t('button.continueWith.label', { provider: provider.name })}
          </button>
        ))}
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">{t('common.or')}</span>
        </div>
      </div>
    </div>
  )
}
